import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "checking", sessionId: null });
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");

  const sendAccessLink = useCallback(async (sid) => {
    if (!sid) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/auth/magic-link`, { stripe_session_id: sid }, { withCredentials: true });
      setSentTo(data?.email || "");
    } catch (err) {
      console.error("magic link after payment failed:", err);
      toast.error("Non riusciamo a inviare il link. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    if (!sid) { setState({ status: "error", sessionId: null }); return; }
    let cancelled = false;
    let tries = 0;
    async function poll() {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", sessionId: sid });
          sendAccessLink(sid);
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", sessionId: sid });
          return;
        }
      } catch (err) {
        console.error("payment status polling failed:", err);
      }
      tries += 1;
      if (tries > 20) { setState({ status: "timeout", sessionId: sid }); return; }
      setTimeout(poll, 2000);
    }
    poll();
    return () => { cancelled = true; };
  }, [loc.search, sendAccessLink]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="tl-paper p-8 max-w-md text-center" data-testid="payment-success">
        {state.status === "checking" && (
          <>
            <Loader2 className="mx-auto text-[#A3B897] animate-spin" size={40} strokeWidth={1.5} />
            <div className="mt-4 font-serif-display text-2xl">Verifica del pagamento…</div>
            <div className="mt-2 text-sm text-[#6A7368]">Solo un momento.</div>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#A3B897]" size={48} strokeWidth={1.5} />
            <div className="mt-4 font-serif-display font-semibold text-3xl">Grazie!</div>
            {sending && (
              <div className="mt-3 text-sm text-[#58635B]">Ti stiamo inviando il link di accesso…</div>
            )}
            {!sending && sentTo && (
              <div className="mt-3 text-sm text-[#58635B]" data-testid="payment-link-sent">
                Pagamento riuscito. Abbiamo inviato il tuo link di accesso a <b className="break-all">{sentTo}</b>:
                aprilo e il planner è tuo. Vale 15 minuti.
              </div>
            )}
            {!sending && !sentTo && (
              <div className="mt-3 text-sm text-[#58635B]">
                Pagamento riuscito. Invia a te stessa il link di accesso per entrare nel planner.
              </div>
            )}
            <button
              onClick={() => sendAccessLink(state.sessionId)}
              disabled={sending}
              className="tl-btn-soft mt-6 inline-flex items-center gap-2 px-6 py-2 bg-white border border-[#E4E7E1] rounded-full text-sm disabled:opacity-60"
              data-testid="resend-access-link"
            >
              <ArrowRight size={14} strokeWidth={1.75} />
              {sentTo ? "Invia di nuovo il link" : "Inviami il link di accesso"}
            </button>
            <div className="mt-3 text-[11px] text-[#6A7368]">Non la trovi? Controlla spam o promozioni.</div>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="mx-auto text-[#C48B97]" size={40} strokeWidth={1.5} />
            <div className="mt-4 font-serif-display text-2xl">Non ci siamo</div>
            <div className="mt-2 text-sm text-[#6A7368]">Il pagamento non risulta completato. Riprova o contattaci.</div>
            <button onClick={() => nav("/")} className="tl-btn-soft mt-6 px-6 py-2 bg-white border border-[#E4E7E1] rounded-full text-sm">
              Torna alla home
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="tl-paper p-8 max-w-md text-center" data-testid="payment-cancel">
        <XCircle className="mx-auto text-[#6A7368]" size={40} strokeWidth={1.5} />
        <div className="mt-4 font-serif-display text-2xl">Pagamento annullato</div>
        <div className="mt-2 text-sm text-[#6A7368]">Puoi riprovare quando vuoi.</div>
        <button onClick={() => nav("/")} className="tl-btn-primary mt-6 px-6 py-2 bg-[#A3B897] hover:bg-[#8FA087] text-white rounded-full text-sm transition-all">
          Torna alla home
        </button>
      </div>
    </div>
  );
}
